/**
 * The help-center index (#2685): article metadata, which article belongs to
 * which admin page, and how article links become in-app routes.
 *
 * The data is generated from `docs/help-center/` by
 * `scripts/build-help-center.mjs` — edit the Markdown, not the JSON.
 */
import indexData from '../../generated/helpCenterIndex.json';

export type HelpSection = 'einstieg' | 'admin' | 'grundlagen' | 'app';
export type HelpArticleType = 'bereich' | 'aufgabe' | 'grundlage' | 'einstieg';

export interface HelpArticleMeta {
    id: string;
    /** `<section>/<file>` — also the route below `/help/`. */
    slug: string;
    section: HelpSection;
    title: string;
    type: HelpArticleType;
    status: string;
    audience: string[];
    useCases: string[];
    requires: string[];
    /** Admin routes on which the "?" button opens this article. */
    routes: string[];
    related: string[];
}

export const HELP_BASE_PATH = '/help';
export const HELP_SECTIONS: HelpSection[] = ['einstieg', 'admin', 'grundlagen', 'app'];

export const helpArticles: HelpArticleMeta[] = (indexData as { articles: HelpArticleMeta[] }).articles;

const bySlug = new Map(helpArticles.map((a) => [a.slug, a]));
const byId = new Map(helpArticles.map((a) => [a.id, a]));

export const findArticleBySlug = (slug: string): HelpArticleMeta | undefined => bySlug.get(slug);
export const findArticleById = (id: string): HelpArticleMeta | undefined => byId.get(id);


export const helpArticlePath = (slug: string, anchor?: string): string => {
    const hash = anchor ? `#${anchor}` : '';
    return `${HELP_BASE_PATH}/${slug}${hash}`;
};

// ── Page → article ("?" button) ─────────────────────────────────────────

/** Score how well a route pattern matches a pathname: -1 for no match,
 *  otherwise the number of literal segments (more literal = more specific).
 *  A `#tab` on the pattern names a section of the page; matching is by path. */
export const matchRoutePattern = (pattern: string, pathname: string): number => {
    const split = (p: string) => p.split('/').filter(Boolean);
    const want = split(pattern.split('#')[0]);
    const have = split(pathname);
    if (want.length !== have.length) return -1;
    let literal = 0;
    for (let i = 0; i < want.length; i += 1) {
        if (want[i].startsWith(':')) continue;
        if (want[i] !== have[i]) return -1;
        literal += 1;
    }
    return literal;
};

const TYPE_PREFERENCE: Record<HelpArticleType, number> = {
    bereich: 3,
    aufgabe: 2,
    einstieg: 1,
    grundlage: 0,
};

/** The article the "?" button should open on `pathname`: the most specific
 *  route match; on a tie an area article ("bereich") beats a task, which
 *  beats a getting-started article, then registry order. */
export const articleForPath = (pathname: string, candidates: HelpArticleMeta[] = helpArticles): HelpArticleMeta | undefined => {
    let best: { article: HelpArticleMeta, score: number, pref: number } | undefined;
    for (const article of candidates) {
        for (const route of article.routes) {
            const score = matchRoutePattern(route, pathname);
            if (score < 0) continue;
            const pref = TYPE_PREFERENCE[article.type] ?? 0;
            if (!best || score > best.score || (score === best.score && pref > best.pref)) {
                best = { article, score, pref };
            }
        }
    }
    return best?.article;
};

// ── Article links ───────────────────────────────────────────────────────

export type ResolvedLink =
    | { kind: 'article', slug: string, anchor?: string }
    | { kind: 'anchor', anchor: string }
    | { kind: 'external', href: string }
    /** Points at an article that is not shipped (hidden) or does not exist —
     *  rendered as plain text rather than a link that leads nowhere. */
    | { kind: 'unavailable' };

/** Resolve a link inside article `fromSlug` (`admin/buchungsuebersicht`). */
export const resolveArticleLink = (fromSlug: string, href: string): ResolvedLink => {
    if (/^(https?:|mailto:)/i.test(href)) return { kind: 'external', href };
    if (href.startsWith('#')) return { kind: 'anchor', anchor: href.slice(1) };

    const [path, anchor] = href.split('#');
    if (!path.endsWith('.md')) return { kind: 'unavailable' };
    const segments = fromSlug.split('/').slice(0, -1);
    for (const part of path.replace(/\.md$/, '').split('/')) {
        if (part === '..') segments.pop();
        else if (part !== '.' && part !== '') segments.push(part);
    }
    const slug = segments.join('/');
    return bySlug.has(slug) ? { kind: 'article', slug, anchor: anchor || undefined } : { kind: 'unavailable' };
};

// ── Home page grouping ──────────────────────────────────────────────────

/** Admin area articles with the task articles each one lists under
 *  `related`; a task appears under the first area that claims it. Tasks no
 *  area claims are returned separately so none goes missing. */
export interface AdminArticleGroups {
    areas: Array<{ area: HelpArticleMeta, tasks: HelpArticleMeta[] }>;
    unclaimed: HelpArticleMeta[];
}

export const groupAdminArticles = (articles: HelpArticleMeta[] = helpArticles): AdminArticleGroups => {
    const admin = articles.filter((a) => a.section === 'admin');
    const claimed = new Set<string>();
    const areas = admin
        .filter((a) => a.type === 'bereich')
        .map((area) => {
            const tasks = area.related
                .map((id) => findArticleById(id))
                .filter((t): t is HelpArticleMeta => !!t && admin.includes(t) && t.type === 'aufgabe' && !claimed.has(t.id));
            tasks.forEach((t) => claimed.add(t.id));
            return { area, tasks };
        });
    const unclaimed = admin.filter((a) => a.type === 'aufgabe' && !claimed.has(a.id));
    return { areas, unclaimed };
};
